import { cn } from "@/lib/cn";

/**
 * Workshop: Agent avatar — circular badge with a letter initial and optional status dot.
 */

type AgentAvatarSize = "sm" | "default" | "lg";

interface AgentAvatarProps {
  letter?: string;
  /** Hex color for filled background (e.g. "#818CF8"). When omitted, uses outline style. */
  color?: string;
  statusColor?: string;
  hideStatus?: boolean;
  size?: AgentAvatarSize;
  className?: string;
  "data-testid"?: string;
}

const sizeMap: Record<
  AgentAvatarSize,
  { wrapper: string; text: string; dot: string; dotPos: string }
> = {
  sm: {
    wrapper: "h-4 w-4",
    text: "text-canvas-2xs",
    dot: "h-1 w-1",
    dotPos: "-right-px -top-px",
  },
  default: {
    wrapper: "h-7 w-7",
    text: "text-canvas-md",
    dot: "h-2 w-2",
    dotPos: "-right-px -top-px",
  },
  lg: {
    wrapper: "h-9 w-9",
    text: "text-canvas-base",
    dot: "h-2.5 w-2.5",
    dotPos: "right-0 top-0",
  },
};

export function AgentAvatar({
  letter = "D",
  color,
  statusColor = "bg-accent",
  hideStatus = false,
  size = "default",
  className,
  "data-testid": testId,
}: AgentAvatarProps) {
  const s = sizeMap[size];
  const isFilled = !!color;

  return (
    <div
      className={cn(
        "relative shrink-0 rounded-full",
        isFilled ? "border border-transparent" : "border border-accent bg-surface",
        s.wrapper,
        className,
      )}
      style={isFilled ? { backgroundColor: color } : undefined}
      data-testid={testId}
    >
      <div className="flex h-full w-full items-center justify-center">
        <span
          className={cn(
            "font-semibold leading-none",
            isFilled ? "text-white" : "text-primary",
            s.text,
          )}
          aria-hidden="true"
        >
          {letter}
        </span>
      </div>
      {!hideStatus && (
        <span
          className={cn(
            "absolute rounded-full border-[1.5px] border-canvas",
            s.dot,
            s.dotPos,
            statusColor,
          )}
          aria-hidden="true"
          data-testid={testId ? `${testId}-status` : undefined}
        />
      )}
    </div>
  );
}

export type { AgentAvatarProps, AgentAvatarSize };
